import { customElement } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import { BaseShape } from './base-shape.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import type { FeatureStyle } from '../../../database/schema.js'

export function buildMarkerHtml(style: FeatureStyle | undefined | null): {
  html: string
  size: [number, number]
  anchor: [number, number]
} {
  const markerType = style?.markerType ?? 'circle'
  const markerIcon = style?.markerIcon ?? ''
  const markerSize = style?.markerSize ?? 12
  const defaultPosition =
    markerType === 'pointer' ? 'above' : markerType === 'triangle' ? 'below' : 'center'
  const markerPosition = style?.markerPosition ?? defaultPosition
  const fillColor = style?.fillColor ?? '#3f5a7a'
  const fillOpacity = style?.fillOpacity ?? 1
  const strokeColor = style?.strokeColor ?? '#ffffff'
  const strokeWidth = style?.strokeWidth ?? 2
  const strokeOpacity = style?.strokeOpacity ?? 1

  const s = markerSize
  const bw = strokeWidth
  const total = s + bw * 2
  const totalHalf = Math.round(total / 2)
  const borderStyle =
    strokeOpacity < 1
      ? `${bw}px solid rgba(${hexToRgb(strokeColor)}, ${strokeOpacity})`
      : `${bw}px solid ${strokeColor}`
  const bg = fillOpacity < 1 ? `rgba(${hexToRgb(fillColor)}, ${fillOpacity})` : fillColor
  const shadow = '0 2px 8px rgba(0,0,0,0.3)'
  const iconFontSize = Math.round(s * 0.55)
  const iconHtml = markerIcon
    ? `<span style="font-family:'Material Symbols Outlined';font-size:${iconFontSize}px;color:${strokeColor};line-height:1;opacity:${strokeOpacity};">${markerIcon}</span>`
    : ''

  let markerHtml = ''
  let iconSize: [number, number] = [total, total]
  let iconAnchor: [number, number] = [totalHalf, totalHalf]

  switch (markerType) {
    case 'none':
      if (markerIcon) {
        const noneIconColor =
          strokeOpacity < 1 ? `rgba(${hexToRgb(strokeColor)}, ${strokeOpacity})` : strokeColor
        markerHtml = `<div style="display:flex;align-items:center;justify-content:center;width:${s}px;height:${s}px;"><span style="font-family:'Material Symbols Outlined';font-size:${s}px;color:${noneIconColor};line-height:1;text-shadow:0 1px 3px rgba(0,0,0,0.4);">${markerIcon}</span></div>`
        iconSize = [s, s]
        iconAnchor = [Math.round(s / 2), Math.round(s / 2)]
      } else {
        const dotTotal = 10 + bw * 2
        markerHtml = `<div style="background:${bg};width:10px;height:10px;border-radius:50%;border:${borderStyle};box-shadow:${shadow};"></div>`
        iconSize = [dotTotal, dotTotal]
        iconAnchor = [Math.round(dotTotal / 2), Math.round(dotTotal / 2)]
      }
      break

    case 'circle':
      markerHtml = `<div style="background:${bg};width:${s}px;height:${s}px;border-radius:50%;border:${borderStyle};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;">${iconHtml}</div>`
      break

    case 'square': {
      const r = Math.round(s * 0.15)
      markerHtml = `<div style="background:${bg};width:${s}px;height:${s}px;border-radius:${r}px;border:${borderStyle};box-shadow:${shadow};display:flex;align-items:center;justify-content:center;">${iconHtml}</div>`
      break
    }

    case 'triangle': {
      const triH = Math.round(s * 0.87)
      const triTotal = s + bw * 2
      const triTotalH = triH + bw * 2
      iconSize = [triTotal, triTotalH]
      iconAnchor = [Math.round(triTotal / 2), Math.round(triTotalH / 2)]
      const triStroke =
        strokeOpacity < 1 ? `rgba(${hexToRgb(strokeColor)}, ${strokeOpacity})` : strokeColor
      const triFill = fillOpacity < 1 ? `rgba(${hexToRgb(fillColor)}, ${fillOpacity})` : fillColor
      const triIconSize = Math.round(s * 0.4)
      const triIconHtml = markerIcon
        ? `<div style="position:absolute;top:${Math.round(triTotalH * 0.4)}px;left:0;width:100%;height:${Math.round(triTotalH * 0.67)}px;display:flex;align-items:flex-start;justify-content:center;z-index:2;"><span style="font-family:'Material Symbols Outlined';font-size:${triIconSize}px;color:${triStroke};line-height:1;">${markerIcon}</span></div>`
        : ''
      markerHtml =
        `<div style="position:relative;width:${triTotal}px;height:${triTotalH}px;">` +
        `<svg style="position:absolute;top:0;left:0;z-index:1;" width="${triTotal}" height="${triTotalH}" viewBox="0 0 ${triTotal} ${triTotalH}">` +
        `<polygon points="${Math.round(triTotal / 2)},${bw} ${triTotal - bw},${triTotalH - bw} ${bw},${triTotalH - bw}" fill="${triFill}" stroke="${triStroke}" stroke-width="${bw}" stroke-linejoin="round"/>` +
        `</svg>` +
        triIconHtml +
        `</div>`
      break
    }

    case 'pointer': {
      const pinW = s + bw * 2
      const pinH = Math.round((s * 20) / 14) + bw * 2
      const pinBody =
        strokeOpacity < 1 ? `rgba(${hexToRgb(strokeColor)}, ${strokeOpacity})` : strokeColor
      const pinCircleFill =
        fillOpacity < 1 ? `rgba(${hexToRgb(fillColor)}, ${fillOpacity})` : fillColor
      iconSize = [pinW, pinH]
      const pinCenterX = Math.round(pinW / 2)
      const circleCenterY = Math.round((pinH * 7) / 20)
      if (markerPosition === 'above') {
        iconAnchor = [pinCenterX, pinH]
      } else if (markerPosition === 'below') {
        iconAnchor = [pinCenterX, 0]
      } else {
        iconAnchor = [pinCenterX, circleCenterY]
      }
      const svgBw = (bw * 14) / s
      const circleR = 7 - svgBw
      const circleCenterPx = Math.round((pinH * 7) / 20)
      const pinIconSize = Math.round((circleR / 7) * s * 0.7)
      const pinIconHtml = markerIcon
        ? `<div style="position:absolute;top:0;left:0;width:100%;height:${circleCenterPx * 2}px;display:flex;align-items:center;justify-content:center;z-index:2;"><span style="font-family:'Material Symbols Outlined';font-size:${pinIconSize}px;color:${pinBody};line-height:1;">${markerIcon}</span></div>`
        : ''
      markerHtml =
        `<div style="position:relative;width:${pinW}px;height:${pinH}px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">` +
        `<svg style="position:absolute;top:0;left:0;z-index:1;" width="${pinW}" height="${pinH}" viewBox="${-svgBw} ${-svgBw} ${14 + svgBw * 2} ${20 + svgBw * 2}">` +
        `<path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${pinBody}" stroke="${pinBody}" stroke-width="${svgBw}"/>` +
        `<circle cx="7" cy="7" r="${circleR}" fill="${pinCircleFill}"/>` +
        `</svg>` +
        pinIconHtml +
        `</div>`
      break
    }
  }

  if (markerType !== 'pointer') {
    if (markerPosition === 'above') {
      iconAnchor = [iconAnchor[0], iconSize[1]]
    } else if (markerPosition === 'below') {
      iconAnchor = [iconAnchor[0], 0]
    }
  }

  return { html: markerHtml, size: iconSize, anchor: iconAnchor }
}

export function hexToRgb(hex: string): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `${r},${g},${b}`
}

@customElement('shape-point')
export class ShapePoint extends BaseShape {
  private pointMarker?: maplibregl.Marker | undefined

  protected createLayers(): void {
    const coords = (this.feature.geometry as GeoJSON.Point).coordinates
    const marker = buildMarkerHtml(this.feature.style)

    const element = document.createElement('div')
    element.className = 'feature-point'
    element.innerHTML = marker.html
    element.style.width = `${marker.size[0]}px`
    element.style.height = `${marker.size[1]}px`
    element.style.cursor = 'pointer'

    const offsetX = marker.size[0] / 2 - marker.anchor[0]
    const offsetY = marker.size[1] / 2 - marker.anchor[1]

    this.pointMarker = new maplibregl.Marker({
      element,
      anchor: 'center',
      offset: [offsetX, -offsetY],
    })
      .setLngLat([coords[0], coords[1]])
      .addTo(this.map)
  }

  protected override setupEventListeners(): void {
    document.addEventListener(
      'shape-selected',
      this.handleOtherShapeSelected.bind(this) as EventListener
    )

    if (this.pointMarker) {
      const el = this.pointMarker.getElement()
      // Point features are DOM markers (invisible to the canvas gesture
      // controller / queryRenderedFeatures), so the marker routes its own taps
      // into the unified model via `user-feature-tapped`.
      el.addEventListener('click', e => {
        e.stopPropagation()
        this.dispatchEvent(
          new CustomEvent('user-feature-tapped', {
            detail: {
              featureId: this.feature.id,
              ctrlKey: e.ctrlKey,
              metaKey: e.metaKey,
              shiftKey: e.shiftKey,
            },
            bubbles: true,
            composed: true,
          })
        )
      })

      // Right-click on the marker → the action menu / identify popup, same as
      // right-clicking the canvas. The gesture controller can't see it (the
      // marker is a DOM element above the canvas), so the marker routes it.
      el.addEventListener('contextmenu', e => {
        e.preventDefault()
        e.stopPropagation()
        const coords = (this.feature.geometry as GeoJSON.Point).coordinates
        this.dispatchEvent(
          new CustomEvent('user-feature-contextmenu', {
            detail: { featureId: this.feature.id, lng: coords[0], lat: coords[1] },
            bubbles: true,
            composed: true,
          })
        )
      })

      let startX = 0,
        startY = 0
      el.addEventListener(
        'touchstart',
        (e: TouchEvent) => {
          if (e.touches.length === 1) {
            startX = e.touches[0].clientX
            startY = e.touches[0].clientY
          }
        },
        { passive: true }
      )
      el.addEventListener(
        'touchend',
        (e: TouchEvent) => {
          if (e.changedTouches.length === 1) {
            const dx = e.changedTouches[0].clientX - startX
            const dy = e.changedTouches[0].clientY - startY
            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
              return
            }
            e.stopPropagation()
            e.preventDefault()
            this.dispatchEvent(
              new CustomEvent('user-feature-tapped', {
                detail: {
                  featureId: this.feature.id,
                  ctrlKey: false,
                  metaKey: false,
                  shiftKey: false,
                },
                bubbles: true,
                composed: true,
              })
            )
          }
        },
        { passive: false }
      )
    }
  }

  protected override handleOtherShapeSelected = (event: Event): void => {
    const customEvent = event as CustomEvent
    if (customEvent.detail.shapeId !== this.feature.id) {
      this.exitEditMode()
    }
  }

  protected override removeEventListeners(): void {
    document.removeEventListener('shape-selected', this.handleOtherShapeSelected)
  }

  override refreshStyle(): void {
    if (!this.pointMarker) {
      return
    }
    const marker = buildMarkerHtml(this.feature.style)
    const element = this.pointMarker.getElement()
    element.innerHTML = marker.html
    element.style.width = `${marker.size[0]}px`
    element.style.height = `${marker.size[1]}px`
  }

  protected createHandles(): void {
    if (!this.pointMarker) {
      return
    }
    const element = this.pointMarker.getElement()
    element.style.outline = '2px solid rgba(59, 130, 246, 0.6)'
    element.style.outlineOffset = '2px'
    element.style.cursor = 'grab'
    this.setupPointDragging()
  }

  private setupPointDragging(): void {
    if (!this.pointMarker) {
      return
    }
    const element = this.pointMarker.getElement()

    element.addEventListener('mousedown', (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      this.startPointDrag()
    })

    element.addEventListener(
      'touchstart',
      (e: TouchEvent) => {
        if (!this.isEditing) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        this.startPointDrag()
      },
      { passive: false }
    )
  }

  private _pointMouseMove?: ((e: MouseEvent) => void) | undefined
  private _pointMouseUp?: ((e: MouseEvent) => void) | undefined
  private _pointTouchMove?: ((e: TouchEvent) => void) | undefined
  private _pointTouchEnd?: ((e: TouchEvent) => void) | undefined

  private startPointDrag(): void {
    if (this.isDragging || !this.isEditing) {
      return
    }
    this.isDragging = true

    this._pointMouseMove = (e: MouseEvent) => {
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      if (this.pointMarker) {
        this.pointMarker.setLngLat(lngLat)
      }
    }
    this._pointMouseUp = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const rect = this.map.getContainer().getBoundingClientRect()
      const lngLat = this.map.unproject([e.clientX - rect.left, e.clientY - rect.top])
      void this.endPointDrag(lngLat)
    }
    this._pointTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        return
      }
      e.preventDefault()
      const lngLat = this.touchToLngLat(e.touches[0])
      this.pointMarker?.setLngLat(lngLat)
    }
    this._pointTouchEnd = (e: TouchEvent) => {
      if (e.changedTouches.length === 0) {
        return
      }
      e.preventDefault()
      const lngLat = this.touchToLngLat(e.changedTouches[0])
      void this.endPointDrag(lngLat)
    }

    document.addEventListener('mousemove', this._pointMouseMove)
    document.addEventListener('mouseup', this._pointMouseUp)
    this.map.getContainer().addEventListener('touchmove', this._pointTouchMove, { passive: false })
    this.map.getContainer().addEventListener('touchend', this._pointTouchEnd, { passive: false })
  }

  private async endPointDrag(lngLat: maplibregl.LngLat): Promise<void> {
    this.isDragging = false
    BaseShape.ignoreNextMapClick++

    if (this._pointMouseMove) {
      document.removeEventListener('mousemove', this._pointMouseMove)
    }
    if (this._pointMouseUp) {
      document.removeEventListener('mouseup', this._pointMouseUp)
    }
    if (this._pointTouchMove) {
      this.map.getContainer().removeEventListener('touchmove', this._pointTouchMove)
    }
    if (this._pointTouchEnd) {
      this.map.getContainer().removeEventListener('touchend', this._pointTouchEnd)
    }
    this._pointMouseMove = undefined
    this._pointMouseUp = undefined
    this._pointTouchMove = undefined
    this._pointTouchEnd = undefined

    await tripFeatureManager.moveFeature(this.feature.id, { lat: lngLat.lat, lng: lngLat.lng })
    ;(this.feature.geometry as GeoJSON.Point).coordinates = [lngLat.lng, lngLat.lat]
  }

  updateSource(): void {}

  protected getGeoJSON(): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: (this.feature.geometry as GeoJSON.Point).coordinates,
      },
    }
  }

  protected updateShapePreview(_handleType: string, _newPosition: maplibregl.LngLat): void {}
  protected async applyGeometryChange(
    _handleType: string,
    _newPosition: maplibregl.LngLat
  ): Promise<void> {}
  protected updateAllHandlePositions(_centerPosition: maplibregl.LngLat): void {}

  override exitEditMode(): void {
    if (!this.isEditing) {
      return
    }
    this.isEditing = false
    if (this.pointMarker) {
      const element = this.pointMarker.getElement()
      element.style.outline = ''
      element.style.outlineOffset = ''
      element.style.cursor = ''
    }
  }

  override setVisible(visible: boolean): void {
    if (this.pointMarker) {
      const el = this.pointMarker.getElement()
      el.style.display = visible ? '' : 'none'
    }
    if (this.labelMarker) {
      if (visible) {
        this.labelMarker.addTo(this.map)
      } else {
        this.labelMarker.remove()
      }
    }
  }

  override removeFromMap(): void {
    if (this.pointMarker) {
      this.pointMarker.remove()
      this.pointMarker = undefined
    }
    if (this.labelMarker) {
      this.labelMarker.remove()
      this.labelMarker = undefined
    }
    this.removeHandles()
  }

  protected override applySelectionStyle(selected: boolean): void {
    if (!this.pointMarker) {
      return
    }
    const el = this.pointMarker.getElement()
    if (selected) {
      el.style.filter =
        'drop-shadow(0 0 4px rgba(37, 99, 235, 0.8)) drop-shadow(0 0 8px rgba(37, 99, 235, 0.4))'
    } else {
      el.style.filter = ''
    }
  }
}
